import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Card, CardContent, Chip,
  IconButton, Alert, FormControl, InputLabel, Select, MenuItem,
  Checkbox, ListItemText, Switch, FormControlLabel, Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Announcement as AnnouncementIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../contexts/UserContext';
import { fetchJson } from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toIsoString = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
};

const AnnouncementsPage = () => {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    roles: [],
    // Portal is always on — an announcement that appears nowhere in the product
    // is not an announcement. Email and SMS are the extra pushes on top of it.
    channels: { portal: true, email: false, sms: false },
    pinned: false,
    scheduledAt: '',
    expiresAt: '',
    priority: 'normal'
  });

  const hasPermission = !!user;

  // datetime-local wants "YYYY-MM-DDTHH:mm" in LOCAL time. Building it from
  // toISOString would hand the browser a UTC moment and shift the bound by the
  // timezone offset — an hour or more of dates wrongly allowed or refused.
  const pad2 = (n) => String(n).padStart(2, '0');
  const asLocal = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
    + `T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const nowLocal = () => asLocal(new Date());
  const minuteAfter = (v) => {
    const d = v ? new Date(v) : new Date();
    if (Number.isNaN(d.getTime())) return nowLocal();
    d.setMinutes(d.getMinutes() + 1);
    return asLocal(d);
  };

  const priorityLabel = (value) => {
    const v = (value || 'normal').toString();
    return t(`announcementsPage.priorities.${v}`);
  };

  const ROLE_LABEL_KEYS = {
    Student: 'announcementsPage.roleOptions.student',
    Parent: 'announcementsPage.roleOptions.parent',
    Teacher: 'announcementsPage.roleOptions.teacher',
    ClassMaster: 'announcementsPage.roleOptions.classMaster',
    SubjectHead: 'announcementsPage.roleOptions.subjectHead',
    Principal: 'announcementsPage.roleOptions.principal',
    HeadMaster: 'announcementsPage.roleOptions.headMaster',
    SchoolAdmin: 'announcementsPage.roleOptions.schoolAdmin',
    FinanceOfficer: 'announcementsPage.roleOptions.financeOfficer',
    AdmissionsOfficer: 'announcementsPage.roleOptions.admissionsOfficer',
    Librarian: 'announcementsPage.roleOptions.librarian',
    TransportOfficer: 'announcementsPage.roleOptions.transportOfficer',
    InventoryManager: 'announcementsPage.roleOptions.inventoryManager',
    NurseHealthOfficer: 'announcementsPage.roleOptions.nurseHealthOfficer',
    ITSupport: 'announcementsPage.roleOptions.itSupport',
    SchoolITCoordinator: 'announcementsPage.roleOptions.schoolItCoordinator',
    Accountant: 'announcementsPage.roleOptions.accountant'
  };

  const roleLabel = (role) => {
    const k = role ? ROLE_LABEL_KEYS[String(role)] : '';
    return k ? t(k) : String(role || '');
  };

  const rolesLabel = (roles) => {
    if (Array.isArray(roles) && roles.length) {
      const s = roles.slice(0, 3).map(roleLabel).join(', ');
      const suffix = roles.length > 3 ? '…' : '';
      return t('announcementsPage.rolesLabel', { roles: `${s}${suffix}` });
    }
    return t('announcementsPage.roles');
  };

  // ONLY ROLES SOMEBODY ACTUALLY HOLDS, in the phase in view.
  //
  // This was a fixed list of seventeen, offered whether or not the school
  // employs anyone in them — announcing to Librarians at a school with no
  // librarian is a message sent to nobody and reported as sent. Against this
  // school's live data, eleven of the seventeen reached nobody.
  //
  // The fallback stands only when the endpoint cannot be reached: a form with
  // no roles at all cannot be submitted, and failing shut over a secondary
  // request is worse than offering the four roles every school has.
  const FALLBACK_ROLES = ['Student', 'Parent', 'Teacher', 'SchoolAdmin'];
  const roleOptions = audiences.length ? audiences.map((a) => a.role) : FALLBACK_ROLES;
  const roleCount = (role) => (audiences.find((a) => a.role === role) || {}).count || 0;

  const loadAnnouncements = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      const [data, who] = await Promise.all([
        fetchJson('/api/announcements'),
        // Who there is to announce TO, in this phase.
        fetchJson('/api/announcements/audiences').catch(() => ({ audiences: [] })),
      ]);
      setAnnouncements(data.announcements || []);
      setAudiences(Array.isArray(who?.audiences) ? who.audiences : []);
      setLoading(false);
    } catch (err) {
      setError(t('announcementsPage.loadFail'));
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (hasPermission) loadAnnouncements();
  }, [hasPermission, loadAnnouncements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!Array.isArray(formData.roles) || formData.roles.length === 0) {
        setError(t('announcementsPage.selectRole'));
        return;
      }
      // Checked here too: `min` bounds the picker, not a value already sitting
      // in state from an older record that was saved before these rules.
      if (formData.scheduledAt && new Date(formData.scheduledAt) < new Date(nowLocal())) {
        setError(t('announcementsPage.sendNotPast', 'The send date cannot be in the past.'));
        return;
      }
      if (formData.expiresAt) {
        const from = formData.scheduledAt ? new Date(formData.scheduledAt) : new Date();
        if (new Date(formData.expiresAt) <= from) {
          setError(t('announcementsPage.expiryAfterSend', 'It must stop showing after the moment it is sent.'));
          return;
        }
      }
      const payload = {
        title: formData.title,
        body: formData.body,
        roles: formData.roles,
        channels: {
          portal: true,
          email: Boolean(formData.channels?.email),
          sms: Boolean(formData.channels?.sms),
        },
        pinned: !!formData.pinned,
        priority: formData.priority
      };
      const scheduledIso = toIsoString(formData.scheduledAt);
      const expiresIso = toIsoString(formData.expiresAt);
      if (scheduledIso) payload.scheduledAt = scheduledIso;
      if (expiresIso) payload.expiresAt = expiresIso;

      if (editingAnnouncement) {
        await fetchJson(`/api/announcements/${editingAnnouncement.id}`, {
          method: 'PUT',
          body: payload
        });
      } else {
        await fetchJson('/api/announcements', {
          method: 'POST',
          body: payload
        });
      }
      
      setDialogOpen(false);
      setEditingAnnouncement(null);
      setFormData({
        title: '',
        body: '',
        roles: [],
        channels: { portal: true, email: false, sms: false },
        pinned: false,
        scheduledAt: '',
        expiresAt: '',
        priority: 'normal'
      });
      loadAnnouncements();
    } catch (err) {
      setError(t('announcementsPage.saveFail'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('announcementsPage.deleteConfirm'))) {
      try {
        await fetchJson(`/api/announcements/${id}`, {
          method: 'DELETE'
        });
        loadAnnouncements();
      } catch (err) {
        setError(t('announcementsPage.deleteFail'));
      }
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      body: announcement.body || announcement.content || '',
      roles: Array.isArray(announcement.roles) ? announcement.roles : [],
      channels: {
        portal: true,
        email: Boolean(announcement.channels?.email),
        sms: Boolean(announcement.channels?.sms),
      },
      pinned: !!announcement.pinned,
      scheduledAt: toDateTimeLocalValue(announcement.scheduledAt),
      expiresAt: toDateTimeLocalValue(announcement.expiresAt),
      priority: announcement.priority || 'normal'
    });
    setDialogOpen(true);
  };

  if (loading && (!announcements || announcements.length === 0)) {
    return (
      <AppLoader variant="circular3" size={60} centered minHeight="60vh" messages={[]} />
    );
  }

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('announcementsPage.noPermission')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Typography variant="h4" component="h1">
          <AnnouncementIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('announcementsPage.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingAnnouncement(null);
            setFormData({
              title: '',
              body: '',
              roles: [],
              channels: { portal: true, email: false, sms: false },
              pinned: false,
              scheduledAt: '',
              expiresAt: '',
              priority: 'normal'
            });
            setDialogOpen(true);
          }}
        >
          {t('announcementsPage.newAnn')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && (!announcements || announcements.length === 0) && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('announcementsPage.none')}
        </Alert>
      )}

      {/* WHAT THIS SCHOOL HAS SENT.
          The analytics that stood here came from the platform's own page and
          answered a question a school cannot act on — engagement BY SCHOOL,
          across schools that are not theirs. What a school administrator needs
          is the state of their own notices: what has gone out, what is waiting
          to, and what has expired and is no longer being shown.

          Counted from the list below rather than fetched, so the figures and
          the cards can never disagree. */}
      {!loading && announcements.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {(() => {
              const now = Date.now();
              const stateOf = (a) => {
                const exp = a?.expiresAt ? new Date(a.expiresAt).getTime() : null;
                if (exp && !Number.isNaN(exp) && exp <= now) return 'expired';
                const at = a?.scheduledAt ? new Date(a.scheduledAt).getTime() : null;
                if (at && !Number.isNaN(at) && at > now) return 'scheduled';
                return 'sent';
              };
              const sent = announcements.filter((a) => stateOf(a) === 'sent').length;
              const scheduled = announcements.filter((a) => stateOf(a) === 'scheduled').length;
              const expired = announcements.filter((a) => stateOf(a) === 'expired').length;
              const emailed = announcements.filter((a) => a.channels?.email).length;
              const texted = announcements.filter((a) => a.channels?.sms).length;
              const cell = (label, value, color) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="h6" sx={color ? { color } : undefined}>{value}</Typography>
                </Box>
              );
              return [
                cell(t('announcementsPage.statSent', 'Live now'), sent, 'success.main'),
                cell(t('announcementsPage.statScheduled', 'Waiting to go out'), scheduled, scheduled > 0 ? 'warning.main' : undefined),
                cell(t('announcementsPage.statExpired', 'Expired'), expired),
                cell(t('announcementsPage.statEmailed', 'Also emailed'), emailed),
                cell(t('announcementsPage.statTexted', 'Also texted'), texted),
              ];
            })()}
          </Box>
        </Paper>
      )}

      <Grid container spacing={3}>
        {announcements.map((announcement) => (
          <Grid item xs={12} md={6} lg={4} key={announcement.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h3">
                    {announcement.title}
                  </Typography>
                  <Box>
                    <IconButton size="small" onClick={() => handleEdit(announcement)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(announcement.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {announcement.body || announcement.content}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {announcement.pinned && (
                    <Chip label={t('announcementsPage.pinned')} color="primary" size="small" />
                  )}
                  <Chip
                    label={priorityLabel(announcement.priority)}
                    color={announcement.priority === 'high' ? 'error' : announcement.priority === 'medium' ? 'warning' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={rolesLabel(announcement.roles)}
                    size="small"
                  />
                  {/* Which channels it actually went out on, so an in-app
                      notice can be told from one that was also texted. */}
                  {announcement.channels?.email && (
                    <Chip label={t('announcementsPage.channelEmail', 'Email')} size="small" variant="outlined" />
                  )}
                  {announcement.channels?.sms && (
                    <Chip label={t('announcementsPage.channelSms', 'SMS')} size="small" variant="outlined" />
                  )}
                  {announcement.scheduledAt && (
                    <Chip
                      label={t('announcementsPage.scheduled')}
                      icon={<ScheduleIcon />}
                      size="small"
                    />
                  )}
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {t('announcementsPage.createdAt')}: {new Date(announcement.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAnnouncement ? t('announcementsPage.editAnn') : t('announcementsPage.newAnn')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('announcementsPage.msgtitle')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('announcementsPage.body')}
                  multiline
                  // The message is the point of the form. Eight rows shows a
                  // paragraph; four showed the first sentence of one.
                  rows={8}
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="roles-label">{t('announcementsPage.roles')}</InputLabel>
                  <Select
                    labelId="roles-label"
                    multiple
                    value={formData.roles}
                    label={t('announcementsPage.roles')}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData({ ...formData, roles: Array.isArray(v) ? v : [] });
                    }}
                    renderValue={(selected) => (Array.isArray(selected) ? selected.map(roleLabel).join(', ') : '')}
                  >
                    {roleOptions.map((r) => (
                      <MenuItem key={r} value={r}>
                        <Checkbox checked={formData.roles.indexOf(r) > -1} />
                        {/* The count is part of the decision: "Parents (7)" and
                            "Parents" are different things to click. */}
                        <ListItemText
                          primary={roleCount(r) ? `${roleLabel(r)} (${roleCount(r)})` : roleLabel(r)}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.pinned}
                      onChange={(_, v) => setFormData({ ...formData, pinned: !!v })}
                    />
                  }
                  label={t('announcementsPage.pin')}
                />
              </Grid>

              {/* HOW IT REACHES THEM. Three channels, not four: there was also
                  a "mobile"/app channel, which was a distinction without a
                  difference — the portal IS the app, the same announcement read
                  on a phone. Ticking one and not the other described a delivery
                  the system does not make.

                  Portal is shown fixed on rather than hidden, so the reader can
                  see it is happening; email and SMS are the extra pushes. */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('announcementsPage.channels', 'How it reaches them')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={<Switch checked disabled />}
                    label={t('announcementsPage.channelPortal', 'In the app and portal')}
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={!!formData.channels?.email}
                        onChange={(_, v) => setFormData({
                          ...formData,
                          channels: { ...(formData.channels || {}), portal: true, email: !!v },
                        })}
                      />
                    )}
                    label={t('announcementsPage.channelEmail', 'Email')}
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={!!formData.channels?.sms}
                        onChange={(_, v) => setFormData({
                          ...formData,
                          channels: { ...(formData.channels || {}), portal: true, sms: !!v },
                        })}
                      />
                    )}
                    label={t('announcementsPage.channelSms', 'SMS')}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {t('announcementsPage.channelHint', 'Everyone sees it in the app and portal. Email and SMS push it out as well.')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label={t('announcementsPage.scheduleForOptional')}
                  value={formData.scheduledAt}
                  onChange={(e) => setFormData({
                    ...formData,
                    scheduledAt: e.target.value,
                    // Pushing the send date past the expiry would leave a
                    // notice that expires before it is sent; the expiry gives
                    // way rather than the pair being left contradicting itself.
                    expiresAt: formData.expiresAt && e.target.value
                      && formData.expiresAt <= e.target.value ? '' : formData.expiresAt,
                  })}
                  // NOT IN THE PAST: scheduling for last week is not a
                  // schedule — the server sends it immediately, which is not
                  // what the date says.
                  inputProps={{ min: nowLocal() }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label={t('announcementsPage.expiresAtOptional')}
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  // AFTER the send moment, never on it: an announcement that
                  // expires as it goes out is one nobody has time to read.
                  inputProps={{ min: minuteAfter(formData.scheduledAt || nowLocal()) }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="priority-label">{t('announcementsPage.priority')}</InputLabel>
                  <Select
                    labelId="priority-label"
                    label={t('announcementsPage.priority')}
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <MenuItem value="low">{t('announcementsPage.priorities.low')}</MenuItem>
                    <MenuItem value="normal">{t('announcementsPage.priorities.normal')}</MenuItem>
                    <MenuItem value="medium">{t('announcementsPage.priorities.medium')}</MenuItem>
                    <MenuItem value="high">{t('announcementsPage.priorities.high')}</MenuItem>
                    <MenuItem value="urgent">{t('announcementsPage.priorities.urgent')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{t('announcementsPage.cancel')}</Button>
            <Button type="submit" variant="contained">
              {editingAnnouncement ? t('announcementsPage.update') : t('announcementsPage.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default AnnouncementsPage;
